// @ts-nocheck
// Legacy editor — superseded by WritingTool.tsx. Kept for story_editor route.
import React, { useEffect, useState } from 'react';
import {
  ArrowLeft,
  Save,
  Plus,
  Loader2,
  Tv,
  Radio,
  Globe2,
  Share2,
  Zap,
  AlertTriangle,
  Clock,
  History,
  Languages,
  ChevronDown,
  CheckCircle,
  Send,
  FileText,
  UserCircle,
  Sparkles,
  RotateCcw,
  Wand2,
  Split,
  MessageSquareWarning,
  RefreshCw,
  Check,
  ArrowRight,
} from 'lucide-react';
import { diffWords, getDiffStats } from '../utils/diff';
import {
  createStory,
  getStoryWithContent,
  updateStory,
  createContentItem,
  createContentVersion,
  getContentItem,
  updateWorkflowStatus,
} from '../services/store';
import { runJournalistAgent, runCriticAgent } from '../services/geminiService';
import {
  Story,
  StoryWithContent,
  ContentItemWithVersion,
  LanguageCode,
  Platform,
  ItemType,
  WorkflowStatus,
  NinjsPayload,
  EditorIssue,
  LANGUAGE_LABELS,
  LANGUAGE_NAMES,
  WORKFLOW_STATUS_LABELS,
  WORKFLOW_STATUS_COLORS,
  getUrgencyLabel,
  formatDuration,
  estimateDuration,
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGES,
  SUPPORTED_PLATFORMS,
} from '../types';

interface StoryEditorProps {
  storyId?: string;
  onExit: () => void;
}

const StoryEditor: React.FC<StoryEditorProps> = ({ storyId, onExit }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [story, setStory] = useState<StoryWithContent | null>(null);
  const [activeContentId, setActiveContentId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Story form state
  const [slug, setSlug] = useState('');
  const [canonicalLanguage, setCanonicalLanguage] = useState<LanguageCode>(DEFAULT_LANGUAGE);
  const [urgency, setUrgency] = useState(5);
  const [isBreaking, setIsBreaking] = useState(false);
  const [breakingLevel, setBreakingLevel] = useState<'flash' | 'urgent' | 'developing'>('developing');

  // Content editing state
  const [headline, setHeadline] = useState('');
  const [summary, setSummary] = useState('');
  const [bodyText, setBodyText] = useState('');

  // New content item dialog - batch creation
  const [showNewContentDialog, setShowNewContentDialog] = useState(false);
  // Matrix selection: which platform+language combos to create
  const [selectedCombos, setSelectedCombos] = useState<Set<string>>(new Set());

  // AI Review state
  const [aiReviewLoading, setAiReviewLoading] = useState(false);
  const [aiReviewLoadingMsg, setAiReviewLoadingMsg] = useState('');
  const [aiReviewIssues, setAiReviewIssues] = useState<EditorIssue[]>([]);
  const [showAiReviewPanel, setShowAiReviewPanel] = useState(false);
  const [customIssue, setCustomIssue] = useState('');

  // Original content for diff comparison
  const [originalHeadline, setOriginalHeadline] = useState('');
  const [originalSummary, setOriginalSummary] = useState('');
  const [originalBody, setOriginalBody] = useState('');

  // Diff view toggle
  const [showDiff, setShowDiff] = useState(false);

  // Lock body scroll while AI overlay is active
  useEffect(() => {
    if (aiReviewLoading) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [aiReviewLoading]);

  // Default content type per platform
  const getDefaultContentType = (platform: Platform): ItemType => {
    switch (platform) {
      case 'tv': return 'video';
      case 'radio': return 'audio';
      case 'web': return 'text';
      case 'social': return 'text';
      default: return 'text';
    }
  };

  const aiOverlay = aiReviewLoading ? (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-zinc-900 rounded-xl shadow-2xl px-6 py-5 w-[340px] text-center border border-zinc-200 dark:border-zinc-700">
        <div className="mx-auto mb-3 h-10 w-10 rounded-full bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-blue-600 dark:text-blue-300" />
        </div>
        <p className="text-sm font-semibold text-zinc-900 dark:text-white">AI agents are working</p>
        <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">{aiReviewLoadingMsg || 'Polishing your story…'}</p>
      </div>
    </div>
  ) : null;

  useEffect(() => {
    if (storyId) {
      loadStory(storyId);
    } else {
      setLoading(false);
    }
  }, [storyId]);

  const loadStory = async (id: string, preserveActiveContent = false) => {
    const previousActiveId = activeContentId;
    setLoading(true);
    try {
      const data = await getStoryWithContent(id);
      if (data) {
        setStory(data);
        setSlug(data.slug);
        setCanonicalLanguage(data.canonical_language);
        setUrgency(data.urgency);
        setIsBreaking(data.is_breaking);
        if (data.breaking_level) {
          setBreakingLevel(data.breaking_level);
        }

        // If preserving active content, try to re-select the same item
        if (preserveActiveContent && previousActiveId && data.content_items) {
          const previousItem = data.content_items.find(i => i.id === previousActiveId);
          if (previousItem) {
            selectContentItem(previousItem);
            return;
          }
        }

        // Auto-select first content item only on initial load
        if (data.content_items && data.content_items.length > 0 && !preserveActiveContent) {
          selectContentItem(data.content_items[0]);
        }
      }
    } catch (error) {
      console.error('Error loading story:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectContentItem = (item: ContentItemWithVersion) => {
    setActiveContentId(item.id);
    if (item.current_version) {
      setHeadline(item.current_version.headline || '');
      setSummary(item.current_version.summary || '');
      setBodyText(item.current_version.body_text || '');
    } else {
      setHeadline('');
      setSummary('');
      setBodyText('');
    }
  };

  const handleSaveStory = async () => {
    setSaving(true);
    try {
      if (!storyId) {
        // Create new story
        const newStory = await createStory({
          slug,
          canonical_language: canonicalLanguage,
          urgency,
          is_breaking: isBreaking,
          breaking_level: isBreaking ? breakingLevel : undefined,
        });

        if (newStory) {
          // Reload to get full story with content
          await loadStory(newStory.id);
        }
      } else {
        // Update existing story
        await updateStory(storyId, {
          slug,
          canonical_language: canonicalLanguage,
          urgency,
          is_breaking: isBreaking,
          breaking_level: isBreaking ? breakingLevel : undefined,
        });
      }
    } catch (error) {
      console.error('Error saving story:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveContent = async () => {
    if (!activeContentId) return;

    setSaving(true);
    try {
      const wordCount = bodyText.split(/\s+/).filter(Boolean).length;
      const activeContent = story?.content_items?.find((i) => i.id === activeContentId);

      const ninjsPayload: NinjsPayload = {
        headline,
        body_text: bodyText,
        description_text: summary,
        language: activeContent?.language,
      };

      const version = await createContentVersion({
        content_item_id: activeContentId,
        headline,
        summary,
        body_text: bodyText,
        body_ninjs: ninjsPayload,
        duration_seconds: Math.round(estimateDuration(wordCount, activeContent?.item_type)),
        version_type: 'update',
      });

      console.log('Content version saved:', version);

      // Reload story to get updated content, preserving active selection
      if (story?.id) {
        await loadStory(story.id, true);
      }
    } catch (error) {
      console.error('Error saving content:', error);
    } finally {
      setSaving(false);
    }
  };

  // Toggle a platform+language combo
  const toggleCombo = (platform: Platform, language: LanguageCode) => {
    const key = `${platform}:${language}`;
    const newSet = new Set(selectedCombos);
    if (newSet.has(key)) {
      newSet.delete(key);
    } else {
      newSet.add(key);
    }
    setSelectedCombos(newSet);
  };

  // Toggle entire platform (all languages)
  const togglePlatform = (platform: Platform) => {
    const platformKeys = SUPPORTED_LANGUAGES.map(l => `${platform}:${l}`);
    const allSelected = platformKeys.every(k => selectedCombos.has(k));

    const newSet = new Set(selectedCombos);
    if (allSelected) {
      platformKeys.forEach(k => newSet.delete(k));
    } else {
      platformKeys.forEach(k => newSet.add(k));
    }
    setSelectedCombos(newSet);
  };

  // Toggle entire language (all platforms)
  const toggleLanguage = (language: LanguageCode) => {
    const platforms = SUPPORTED_PLATFORMS.filter(p => p !== 'all');
    const langKeys = platforms.map(p => `${p}:${language}`);
    const allSelected = langKeys.every(k => selectedCombos.has(k));

    const newSet = new Set(selectedCombos);
    if (allSelected) {
      langKeys.forEach(k => newSet.delete(k));
    } else {
      langKeys.forEach(k => newSet.add(k));
    }
    setSelectedCombos(newSet);
  };

  // Check if combo already exists
  const comboExists = (platform: Platform, language: LanguageCode) => {
    return story?.content_items?.some(
      item => item.platform === platform && item.language === language
    );
  };

  const handleCreateContentItems = async () => {
    if (!story?.id) {
      setErrorMessage('Story must be saved before adding content items');
      return;
    }
    if (selectedCombos.size === 0) {
      setErrorMessage('Please select at least one platform/language combination');
      return;
    }

    setErrorMessage(null);
    setSuccessMessage(null);
    setSaving(true);

    try {
      const isFirstItem = story.content_items?.length === 0;
      let firstNewItem: any = null;
      let createdCount = 0;
      let skippedCount = 0;
      let failedCount = 0;
      let index = 0;

      for (const combo of selectedCombos) {
        const [platform, language] = combo.split(':') as [Platform, LanguageCode];

        // Skip if already exists
        if (comboExists(platform, language)) {
          skippedCount++;
          continue;
        }

        console.log(`Creating content item: ${platform}/${language} for story ${story.id}`);

        const newItem = await createContentItem({
          story_id: story.id,
          language,
          platform,
          item_type: getDefaultContentType(platform),
          is_canonical: isFirstItem && index === 0,
        });

        if (newItem) {
          createdCount++;
          if (!firstNewItem) {
            firstNewItem = newItem;
          }
        } else {
          failedCount++;
          console.error(`Failed to create content item: ${platform}/${language}`);
        }
        index++;
      }

      setShowNewContentDialog(false);
      setSelectedCombos(new Set());
      await loadStory(story.id);

      // Show feedback
      if (createdCount > 0) {
        setSuccessMessage(`Created ${createdCount} content item${createdCount > 1 ? 's' : ''}`);
      }
      if (failedCount > 0) {
        setErrorMessage(`Failed to create ${failedCount} content item${failedCount > 1 ? 's' : ''}. Check browser console for details.`);
      }
      if (skippedCount > 0 && createdCount === 0 && failedCount === 0) {
        setErrorMessage('All selected combinations already exist');
      }

      // Select the first new item
      if (firstNewItem) {
        const item = await getContentItem(firstNewItem.id);
        if (item) {
          selectContentItem(item);
        }
      }
    } catch (error) {
      console.error('Error creating content items:', error);
      setErrorMessage(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleWorkflowAction = async (newStatus: WorkflowStatus) => {
    if (!activeContentId) return;

    // If transitioning to AI Review, run the AI agents
    if (newStatus === 'ai_review') {
      await runAiReview();
      return;
    }

    setSaving(true);
    try {
      const result = await updateWorkflowStatus(activeContentId, newStatus);
      console.log('Workflow status updated:', newStatus, result);
      if (story?.id) {
        await loadStory(story.id, true);  // Preserve active content selection
      }
    } catch (error) {
      console.error('Error updating workflow status:', error);
    } finally {
      setSaving(false);
    }
  };

  // Run AI Review: First polish with journalist agent, then critique
  const runAiReview = async () => {
    if (!activeContentId) return;

    const activeContent = story?.content_items?.find((i) => i.id === activeContentId);
    if (!activeContent) return;

    // Check if there's content to review
    if (!bodyText.trim()) {
      setErrorMessage('Please write some content before running AI Review.');
      return;
    }

    setAiReviewLoading(true);
    setAiReviewLoadingMsg('Running Journalist Agent…');
    setShowAiReviewPanel(true);
    setAiReviewIssues([]);
    setErrorMessage(null);

    // Store original content for diff comparison
    const currentHeadline = headline;
    const currentSummary = summary;
    const currentBody = bodyText;

    // Save originals for diff view
    setOriginalHeadline(currentHeadline);
    setOriginalSummary(currentSummary);
    setOriginalBody(currentBody);

    try {
      console.log('Starting AI Review for content:', activeContentId);
      console.log('Content language:', activeContent.language);

      // Step 0: Auto-save content first so it's not lost
      const wordCount = currentBody.split(/\s+/).filter(Boolean).length;
      const ninjsPayload: NinjsPayload = {
        headline: currentHeadline,
        body_text: currentBody,
        description_text: currentSummary,
        language: activeContent.language,
      };

      await createContentVersion({
        content_item_id: activeContentId,
        headline: currentHeadline,
        summary: currentSummary,
        body_text: currentBody,
        body_ninjs: ninjsPayload,
        duration_seconds: Math.round(estimateDuration(wordCount, activeContent.item_type)),
        version_type: 'update',
      });

      // Determine if this is a translation (derived from another content item or not canonical)
      const isTranslation = !activeContent.is_canonical || !!activeContent.derived_from_content_id;
      
      // Step 1: Polish with Journalist Agent
      setAiReviewLoadingMsg('Running Journalist Agent…');
      const polished = await runJournalistAgent(
        currentHeadline,
        currentSummary,
        currentBody,
        activeContent.language,
        []  // No specific feedback, just polish
      );

      console.log('Journalist Agent result:', polished);

      // Apply polished content directly to the editor (like main branch)
      if (polished && !('error' in polished && polished.error)) {
        setHeadline(polished.headline || currentHeadline);
        setSummary(polished.deck || currentSummary);
        setBodyText(polished.body || currentBody);
      }

      // Step 2: Run Critic Agent to identify issues
      // For translations, Critic should only check language/grammar, not content/structure
      setAiReviewLoadingMsg('Running Critic Agent…');
      const critique = await runCriticAgent(
        polished && !('error' in polished && polished.error) 
          ? (polished.headline || currentHeadline)
          : currentHeadline,
        polished && !('error' in polished && polished.error)
          ? (polished.deck || currentSummary)
          : currentSummary,
        polished && !('error' in polished && polished.error)
          ? (polished.body || currentBody)
          : currentBody,
        activeContent.language,
        isTranslation  // Pass correct translation flag
      );

      console.log('Journalist Agent result:', polished);
      console.log('Critic Agent result:', critique);

      // Apply polished content directly to the editor (like main branch)
      if (polished && !('error' in polished && polished.error)) {
        setHeadline(polished.headline || currentHeadline);
        setSummary(polished.deck || currentSummary);
        setBodyText(polished.body || currentBody);
      }

      // Store issues with proper categorization
      if (critique && critique.issues) {
        setAiReviewIssues(critique.issues.map((issue, idx) => ({
          id: issue.id || `issue-${idx}`,
          category: issue.category || 'human_required',
          text: typeof issue === 'string' ? issue : issue.text,
          suggestion: issue.suggestion,
          applied: false,
        })));
      }

      // Update workflow status to ai_review
      await updateWorkflowStatus(activeContentId, 'ai_review');

      // Reload story but DON'T reset the form fields
      if (story?.id) {
        const data = await getStoryWithContent(story.id);
        if (data) {
          setStory(data);
        }
      }
    } catch (error) {
      console.error('AI Review failed:', error);
      setErrorMessage(`AI Review failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setAiReviewLoading(false);
      setAiReviewLoadingMsg('');
    }
  };

  // Remove an issue from the list
  const removeIssue = (issueId: string) => {
    setAiReviewIssues(prev => prev.filter(issue => issue.id !== issueId));
  };

  // Add a custom issue/note
  const addCustomIssue = () => {
    if (customIssue.trim()) {
      setAiReviewIssues(prev => [...prev, {
        id: `custom-${Date.now()}`,
        category: 'custom' as const,
        text: customIssue.trim(),
        applied: false,
      }]);
      setCustomIssue('');
    }
  };

  // Get counts by category
  const getIssueCounts = () => {
    const aiFixable = aiReviewIssues.filter(i => i.category === 'ai_fixable' && !i.applied);
    const humanRequired = aiReviewIssues.filter(i => i.category === 'human_required' && !i.applied);
    const custom = aiReviewIssues.filter(i => i.category === 'custom' && !i.applied);
    return { aiFixable, humanRequired, custom };
  };

  // Apply feedback - re-run journalist agent with AI-FIXABLE issues only
  const applyFeedback = async () => {
    const { aiFixable, custom } = getIssueCounts();
    const issuesToApply = [...aiFixable, ...custom]; // AI can address ai_fixable + custom notes

    if (issuesToApply.length === 0 || !activeContentId) {
      setErrorMessage('No AI-fixable issues to apply. Human-required issues need manual attention.');
      return;
    }

    const activeContent = story?.content_items?.find((i) => i.id === activeContentId);
    if (!activeContent) return;

    setAiReviewLoading(true);
    setAiReviewLoadingMsg('Running Journalist Agent… (applying feedback)');
    setErrorMessage(null);

    try {
      console.log('Applying feedback with AI-fixable issues:', issuesToApply.map(i => i.text));

      // Run journalist agent with the AI-fixable issues as feedback
      const polished = await runJournalistAgent(
        headline,
        summary,
        bodyText,
        activeContent.language,
        issuesToApply.map(issue => issue.text)  // Pass issue texts as feedback
      );

      console.log('Journalist Agent result after feedback:', polished);

      // Apply the polished content directly
      if (polished) {
        setHeadline(polished.headline || headline);
        setSummary(polished.deck || summary);
        setBodyText(polished.body || bodyText);
      }

      // Mark the applied issues as applied, keep human_required issues visible
      setAiReviewIssues(prev => prev.map(issue =>
        issuesToApply.some(a => a.id === issue.id)
          ? { ...issue, applied: true }
          : issue
      ).filter(issue => !issue.applied)); // Remove applied issues

      setSuccessMessage(`Applied ${issuesToApply.length} issue(s). Human-required items remain for your attention.`);
    } catch (error) {
      console.error('Apply feedback failed:', error);
      setErrorMessage(`Apply feedback failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setAiReviewLoading(false);
      setAiReviewLoadingMsg('');
    }
  };

  // Finish review - proceed to submit
  const handleFinishReview = () => {
    const { aiFixable, custom, humanRequired } = getIssueCounts();
    const unappliedAiIssues = aiFixable.length + custom.length;

    if (unappliedAiIssues > 0) {
      if (!window.confirm(`There are ${unappliedAiIssues} AI-fixable issue(s) that haven't been applied yet. Continue anyway?`)) {
        return;
      }
    }

    if (humanRequired.length > 0) {
      if (!window.confirm(`There are ${humanRequired.length} issue(s) that need your manual attention (research, verification, etc.). Have you addressed them? Continue anyway?`)) {
        return;
      }
    }

    // Save content and submit
    handleSaveContent();
    handleWorkflowAction('submitted');
    setShowAiReviewPanel(false);
    setShowDiff(false);
    setOriginalBody('');
  };

  const getActiveContent = () => {
    return story?.content_items?.find((i) => i.id === activeContentId);
  };

  const getPlatformIcon = (platform: Platform) => {
    switch (platform) {
      case 'tv':
        return <Tv className="h-4 w-4" />;
      case 'radio':
        return <Radio className="h-4 w-4" />;
      case 'web':
        return <Globe2 className="h-4 w-4" />;
      case 'social':
        return <Share2 className="h-4 w-4" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  const getStatusColor = (status: WorkflowStatus) => {
    return WORKFLOW_STATUS_COLORS[status] || '#9CA3AF';
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const activeContent = getActiveContent();

  return (
    <div className="h-full flex flex-col bg-zinc-50 dark:bg-zinc-950 animate-fade-in">
      {/* Header */}
      <div className="bg-white dark:bg-zinc-900 border-b border-zinc-200 dark:border-zinc-800 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={onExit}
              className="p-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-lg transition-colors"
            >
              <ArrowLeft className="h-5 w-5 text-zinc-600 dark:text-zinc-400" />
            </button>
            <div>
              <h1 className="text-lg font-semibold text-zinc-900 dark:text-white">
                {storyId ? 'Edit Story' : 'New Story'}
              </h1>
              {story && (
                <p className="text-sm text-zinc-500 dark:text-zinc-400">{story.slug}</p>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleSaveStory}
              disabled={saving || !slug}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save Story
            </button>
          </div>
        </div>
      </div>

      {/* Error/Success Messages */}
      {errorMessage && (
        <div className="mx-6 mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center justify-between animate-fade-in animate-slide-in-down">
          <div className="flex items-center">
            <AlertTriangle className="h-4 w-4 text-red-500 mr-2" />
            <span className="text-sm text-red-700 dark:text-red-400">{errorMessage}</span>
          </div>
          <button onClick={() => setErrorMessage(null)} className="text-red-500 hover:text-red-700">×</button>
        </div>
      )}
      {successMessage && (
        <div className="mx-6 mt-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-center justify-between animate-fade-in animate-slide-in-down">
          <div className="flex items-center">
            <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
            <span className="text-sm text-green-700 dark:text-green-400">{successMessage}</span>
          </div>
          <button onClick={() => setSuccessMessage(null)} className="text-green-500 hover:text-green-700">×</button>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Story Settings & Content List */}
        <div className="w-80 bg-white dark:bg-zinc-900 border-r border-zinc-200 dark:border-zinc-800 flex flex-col">
          {/* Story Settings */}
          <div className="p-4 border-b border-zinc-200 dark:border-zinc-800">
            <h2 className="text-sm font-semibold text-zinc-900 dark:text-white mb-3">
              Story Settings
            </h2>

            {/* Slug */}
            <div className="mb-3">
              <label className="block text-xs font-medium text-zinc-600 dark:text-zinc-400 mb-1">
                Slug
              </label>
              <input
                type="text"
                value={slug}
                onChange={(e) => setSlug(e.target.value.toUpperCase().replace(/\s+/g, '-'))}
                placeholder="AFG-KABUL-ELECTION-2025"
                className="w-full px-3 py-2 text-sm border border-zinc-200 dark:border-zinc-700 rounded-lg bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Canonical Language */}
            <div className="mb-3">
              <label className="block text-xs font-medium text-zinc-600 dark:text-zinc-400 mb-1">
                Canonical Language
              </label>
              <select
                value={canonicalLanguage}
                onChange={(e) => setCanonicalLanguage(e.target.value as LanguageCode)}
                className="w-full px-3 py-2 text-sm border border-zinc-200 dark:border-zinc-700 rounded-lg bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="fa-AF">{LANGUAGE_NAMES['fa-AF']} ({LANGUAGE_LABELS['fa-AF']})</option>
                <option value="ps-AF">{LANGUAGE_NAMES['ps-AF']} ({LANGUAGE_LABELS['ps-AF']})</option>
                <option value="eng">{LANGUAGE_NAMES['eng']}</option>
              </select>
            </div>

            {/* Urgency */}
            <div className="mb-3">
              <label className="block text-xs font-medium text-zinc-600 dark:text-zinc-400 mb-1">
                Urgency: {getUrgencyLabel(urgency)}
              </label>
              <input
                type="range"
                min="1"
                max="9"
                value={urgency}
                onChange={(e) => setUrgency(Number(e.target.value))}
                className="w-full"
              />
              <div className="flex justify-between text-[10px] text-zinc-400">
                <span>Flash</span>
                <span>Evergreen</span>
              </div>
            </div>

            {/* Breaking News Toggle */}
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-medium text-zinc-600 dark:text-zinc-400">
                Breaking News
              </label>
              <button
                onClick={() => setIsBreaking(!isBreaking)}
                className={`relative w-10 h-5 rounded-full transition-colors ${
                  isBreaking ? 'bg-red-500' : 'bg-zinc-300 dark:bg-zinc-600'
                }`}
              >
                <div
                  className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                    isBreaking ? 'translate-x-5' : 'translate-x-0.5'
                  }`}
                />
              </button>
            </div>

            {isBreaking && (
              <select
                value={breakingLevel}
                onChange={(e) => setBreakingLevel(e.target.value as any)}
                className="w-full px-3 py-2 text-sm border border-red-200 dark:border-red-800 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                <option value="flash">FLASH - Highest Priority</option>
                <option value="urgent">URGENT - High Priority</option>
                <option value="developing">DEVELOPING - In Progress</option>
              </select>
            )}
          </div>

          {/* Content Items List (Work in Progress) */}
          <div className="flex-1 overflow-y-auto">
            <div className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-zinc-900 dark:text-white">
                  Content Items
                </h2>
                {story?.id && (
                  <button
                    onClick={() => setShowNewContentDialog(true)}
                    className="p-1.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                    title="Quick add content (use Planning page for proper workflow)"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                )}
              </div>

              {!story?.id ? (
                <p className="text-sm text-zinc-500 dark:text-zinc-400 text-center py-4">
                  Save the story first
                </p>
              ) : story.content_items?.length === 0 ? (
                <div className="text-center py-6">
                  <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-2">
                    No content yet
                  </p>
                  <p className="text-xs text-zinc-400 dark:text-zinc-500">
                    Create assignments in Planning, then click "Start Working"
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {story.content_items?.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => selectContentItem(item)}
                      className={`w-full text-left p-3 rounded-lg border transition-colors ${
                        activeContentId === item.id
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-zinc-200 dark:border-zinc-700 hover:border-zinc-300 dark:hover:border-zinc-600'
                      }`}
                    >
                      <div className="flex items-center space-x-2 mb-1">
                        <span
                          className={`${
                            item.platform === 'tv'
                              ? 'text-blue-500'
                              : item.platform === 'radio'
                              ? 'text-orange-500'
                              : item.platform === 'web'
                              ? 'text-green-500'
                              : 'text-purple-500'
                          }`}
                        >
                          {getPlatformIcon(item.platform)}
                        </span>
                        <span className="text-sm font-medium text-zinc-900 dark:text-white">
                          {item.platform.toUpperCase()}
                        </span>
                        <span className="px-1.5 py-0.5 text-[10px] bg-zinc-100 dark:bg-zinc-700 text-zinc-500 dark:text-zinc-400 rounded">
                          {LANGUAGE_LABELS[item.language]}
                        </span>
                        {item.is_canonical && (
                          <span className="px-1.5 py-0.5 text-[10px] bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded">
                            Canonical
                          </span>
                        )}
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-zinc-500 dark:text-zinc-400 truncate">
                          {item.current_version?.headline || 'No headline'}
                        </span>
                        <span
                          className="px-1.5 py-0.5 text-[10px] font-medium rounded-full"
                          style={{
                            backgroundColor: `${getStatusColor(item.workflow_status)}20`,
                            color: getStatusColor(item.workflow_status),
                          }}
                        >
                          {WORKFLOW_STATUS_LABELS[item.workflow_status]}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Panel - Content Editor */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {activeContent ? (
            <>
              {/* Content Header */}
              <div className="bg-white dark:bg-zinc-900 border-b border-zinc-200 dark:border-zinc-800 px-6 py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <span
                      className={`p-2 rounded-lg ${
                        activeContent.platform === 'tv'
                          ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
                          : activeContent.platform === 'radio'
                          ? 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400'
                          : activeContent.platform === 'web'
                          ? 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400'
                      }`}
                    >
                      {getPlatformIcon(activeContent.platform)}
                    </span>
                    <div>
                      <h3 className="text-sm font-medium text-zinc-900 dark:text-white">
                        {activeContent.platform.toUpperCase()} - {LANGUAGE_NAMES[activeContent.language]}
                      </h3>
                      <p className="text-xs text-zinc-500 dark:text-zinc-400">
                        {activeContent.item_type} | Version {activeContent.current_version?.version_number || 1}
                      </p>
                    </div>
                  </div>

                  {/* Workflow Actions */}
                  <div className="flex items-center space-x-2">
                    {/* JOURNALIST WORKFLOW: Draft → AI Review → Submit to Editor */}
                    {activeContent.workflow_status === 'draft' && (
                      <button
                        onClick={() => handleWorkflowAction('ai_review')}
                        className="flex items-center px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                        title="Send to AI for review before submitting to editor"
                      >
                        <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                        AI Review
                      </button>
                    )}
                    {activeContent.workflow_status === 'ai_review' && (
                      <>
                        <button
                          onClick={() => handleWorkflowAction('draft')}
                          className="flex items-center px-3 py-1.5 text-sm bg-zinc-200 dark:bg-zinc-700 text-zinc-700 dark:text-zinc-300 rounded-lg hover:bg-zinc-300 dark:hover:bg-zinc-600 transition-colors"
                          title="Return to draft for more edits"
                        >
                          <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                          Back to Draft
                        </button>
                        <button
                          onClick={() => handleWorkflowAction('submitted')}
                          className="flex items-center px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                          title="Formally submit to editor for review"
                        >
                          <Send className="h-3.5 w-3.5 mr-1.5" />
                          Submit to Editor
                        </button>
                      </>
                    )}
                    {/* EDITOR WORKFLOW: Review submitted content */}
                    {activeContent.workflow_status === 'submitted' && (
                      <>
                        <button
                          onClick={() => handleWorkflowAction('needs_revision')}
                          className="flex items-center px-3 py-1.5 text-sm bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 rounded-lg hover:bg-orange-200 dark:hover:bg-orange-900/50 transition-colors"
                        >
                          <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                          Request Revision
                        </button>
                        <button
                          onClick={() => handleWorkflowAction('in_review')}
                          className="flex items-center px-3 py-1.5 text-sm bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition-colors"
                        >
                          <CheckCircle className="h-3.5 w-3.5 mr-1.5" />
                          Begin Review
                        </button>
                      </>
                    )}
                    {activeContent.workflow_status === 'needs_revision' && (
                      <button
                        onClick={() => handleWorkflowAction('draft')}
                        className="flex items-center px-3 py-1.5 text-sm bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors"
                        title="Return to draft to make revisions"
                      >
                        <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                        Resume Editing
                      </button>
                    )}
                    {activeContent.workflow_status === 'in_review' && (
                      <>
                        <button
                          onClick={() => handleWorkflowAction('needs_revision')}
                          className="flex items-center px-3 py-1.5 text-sm bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 rounded-lg hover:bg-orange-200 dark:hover:bg-orange-900/50 transition-colors"
                        >
                          <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                          Request Revision
                        </button>
                        <button
                          onClick={() => handleWorkflowAction('approved')}
                          className="flex items-center px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                        >
                          <CheckCircle className="h-3.5 w-3.5 mr-1.5" />
                          Approve
                        </button>
                      </>
                    )}
                    <button
                      onClick={handleSaveContent}
                      disabled={saving}
                      className="flex items-center px-3 py-1.5 text-sm bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 rounded-lg hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors"
                    >
                      {saving ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                      ) : (
                        <Save className="h-3.5 w-3.5 mr-1.5" />
                      )}
                      Save Content
                    </button>
                  </div>
                </div>
              </div>

              {/* Content Form - with Review mode toolbar when in AI Review */}
              <div className="flex-1 flex flex-col overflow-hidden bg-white dark:bg-zinc-950">
                {/* Review Mode Toolbar - shows when in AI Review */}
                {showAiReviewPanel && (
                  <div className="flex-shrink-0 bg-white dark:bg-zinc-900 px-6 py-3 border-b border-zinc-100 dark:border-zinc-800 flex justify-between items-center z-10">
                    <div className="flex items-center">
                      {showDiff ? <Split className="h-4 w-4 mr-2 text-blue-600 dark:text-blue-400" /> : <Wand2 className="h-4 w-4 mr-2 text-blue-600 dark:text-blue-400"/>}
                      <h3 className="font-semibold text-zinc-800 dark:text-zinc-200 text-sm">
                        {showDiff ? "Comparison View" : "Polished Draft"}
                      </h3>
                    </div>

                    <div className="flex items-center space-x-2">
                      {/* Diff stats badge */}
                      {showDiff && originalBody && (
                        <span className="text-xs text-zinc-500 dark:text-zinc-400 font-medium bg-zinc-100 dark:bg-zinc-800 px-3 py-1.5 rounded-full border border-zinc-200 dark:border-zinc-700">
                          {(() => {
                            const diffs = diffWords(originalBody, bodyText);
                            const stats = getDiffStats(diffs);
                            return (
                              <>
                                <span className="text-green-600 dark:text-green-400">+{stats.added}</span> / <span className="text-red-500 dark:text-red-400">-{stats.removed} words</span>
                              </>
                            );
                          })()}
                        </span>
                      )}
                      <button
                        onClick={() => setShowDiff(!showDiff)}
                        className={`text-xs px-3 py-1.5 rounded-md font-medium transition-colors border ${
                          showDiff
                          ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800'
                          : 'bg-white dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 border-zinc-200 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-700'
                        }`}
                      >
                        {showDiff ? "Hide Changes" : "Show Changes"}
                      </button>
                    </div>
                  </div>
                )}

                {/* Content Editor */}
                <div className="flex-1 overflow-y-auto p-6">
                  <div className="max-w-3xl mx-auto space-y-8">
                    {/* Headline */}
                    <input
                      type="text"
                      value={headline}
                      onChange={(e) => setHeadline(e.target.value)}
                      disabled={showDiff}
                      placeholder="Headline"
                      className={`w-full text-2xl font-bold border-none p-0 focus:ring-0 text-zinc-900 dark:text-white placeholder-zinc-300 dark:placeholder-zinc-700 bg-transparent ${showDiff ? 'cursor-default' : ''}`}
                    />

                    {/* Summary/Deck */}
                    <textarea
                      value={summary}
                      onChange={(e) => setSummary(e.target.value)}
                      disabled={showDiff}
                      rows={2}
                      placeholder="Summary / Deck..."
                      className={`w-full text-lg text-zinc-600 dark:text-zinc-400 border-none p-0 focus:ring-0 resize-none italic bg-transparent ${showDiff ? 'cursor-default' : ''}`}
                    />

                    <div className="w-full h-px bg-zinc-100 dark:bg-zinc-800 my-4" />

                    {/* Body - Show diff view or editable text */}
                    {showDiff && originalBody ? (
                      <div className="text-lg leading-relaxed whitespace-pre-wrap dark:text-zinc-300">
                        {diffWords(originalBody, bodyText).map((part, i) => {
                          if (part.added) return <span key={i} className="bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300 px-0.5 rounded box-decoration-clone">{part.value} </span>;
                          if (part.removed) return <span key={i} className="bg-red-50 dark:bg-red-900/30 text-red-400 dark:text-red-300 line-through decoration-red-400/50 px-0.5 opacity-60 box-decoration-clone">{part.value} </span>;
                          return <span key={i} className="text-zinc-800 dark:text-zinc-200">{part.value} </span>;
                        })}
                      </div>
                    ) : (
                      <textarea
                        value={bodyText}
                        onChange={(e) => setBodyText(e.target.value)}
                        placeholder="Start writing your story..."
                        className="w-full min-h-[500px] text-lg leading-relaxed text-zinc-800 dark:text-zinc-200 placeholder-zinc-200 dark:placeholder-zinc-800 border-none p-0 focus:ring-0 resize-none bg-transparent"
                      />
                    )}

                    {/* Word count / duration */}
                    <div className="flex items-center justify-between text-xs text-zinc-400 dark:text-zinc-500 pt-4 border-t border-zinc-100 dark:border-zinc-800">
                      <span>
                        {bodyText.split(/\s+/).filter(Boolean).length} words
                      </span>
                      <span>
                        Est. duration: {formatDuration(estimateDuration(bodyText.split(/\s+/).filter(Boolean).length, activeContent.item_type))}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <FileText className="h-12 w-12 text-zinc-300 dark:text-zinc-600 mx-auto mb-4" />
                <p className="text-zinc-500 dark:text-zinc-400">
                  {story?.id
                    ? 'Select or create a content item to start editing'
                    : 'Save the story first to add content'}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* AI Review Panel - Right Sidebar (Editor's Notes) */}
        {showAiReviewPanel && (
          <div className="w-80 flex-shrink-0 flex flex-col bg-zinc-50 dark:bg-zinc-900 border-l border-zinc-200 dark:border-zinc-800 transition-colors">
            <div className="px-4 py-3 border-b border-zinc-200 dark:border-zinc-800 bg-zinc-100/50 dark:bg-zinc-800/50">
              <h3 className="font-semibold text-zinc-800 dark:text-zinc-200 text-sm flex items-center">
                <MessageSquareWarning className="h-4 w-4 mr-2 text-orange-500"/> Editor's Notes
              </h3>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {aiReviewLoading ? (
                <div className="flex flex-col items-center justify-center py-10">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600 dark:text-blue-400 mb-4" />
                  <p className="text-sm font-medium text-zinc-700 dark:text-zinc-200">{aiReviewLoadingMsg}</p>
                </div>
              ) : aiReviewIssues.length === 0 ? (
                <div className="text-center py-10 text-zinc-500 dark:text-zinc-400">
                  <Check className="h-8 w-8 mx-auto text-green-500 mb-2"/>
                  <p className="text-sm">No issues found!</p>
                </div>
              ) : (
                <>
                  {/* AI-Fixable Issues */}
                  {aiReviewIssues.filter(i => i.category === 'ai_fixable').length > 0 && (
                    <div className="mb-4">
                      <div className="flex items-center mb-2">
                        <Sparkles className="h-3.5 w-3.5 text-yellow-500 mr-1.5" />
                        <span className="text-xs font-semibold text-yellow-700 dark:text-yellow-400 uppercase tracking-wider">
                          AI Can Fix ({aiReviewIssues.filter(i => i.category === 'ai_fixable').length})
                        </span>
                      </div>
                      {aiReviewIssues.filter(i => i.category === 'ai_fixable').map((issue, idx) => (
                        <div key={issue.id} className="bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded-lg border border-yellow-200 dark:border-yellow-800/50 mb-2 flex items-start group hover:border-yellow-300 dark:hover:border-yellow-700 transition-colors">
                          <span className="text-xs font-bold text-yellow-400 dark:text-yellow-600 mr-2 mt-0.5">{idx + 1}</span>
                          <div className="flex-1">
                            <p className="text-sm text-zinc-700 dark:text-zinc-300 leading-snug">{issue.text}</p>
                            {issue.suggestion && (
                              <p className="text-xs text-yellow-600 dark:text-yellow-500 mt-1 italic">💡 {issue.suggestion}</p>
                            )}
                          </div>
                          <button
                            onClick={() => removeIssue(issue.id!)}
                            className="text-yellow-400 dark:text-yellow-600 hover:text-red-500 dark:hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity ml-1"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Human Required Issues */}
                  {aiReviewIssues.filter(i => i.category === 'human_required').length > 0 && (
                    <div className="mb-4">
                      <div className="flex items-center mb-2">
                        <AlertTriangle className="h-3.5 w-3.5 text-orange-500 mr-1.5" />
                        <span className="text-xs font-semibold text-orange-700 dark:text-orange-400 uppercase tracking-wider">
                          Needs Your Attention ({aiReviewIssues.filter(i => i.category === 'human_required').length})
                        </span>
                      </div>
                      {aiReviewIssues.filter(i => i.category === 'human_required').map((issue, idx) => (
                        <div key={issue.id} className="bg-orange-50 dark:bg-orange-900/20 p-3 rounded-lg border border-orange-200 dark:border-orange-800/50 mb-2 flex items-start group hover:border-orange-300 dark:hover:border-orange-700 transition-colors">
                          <span className="text-xs font-bold text-orange-400 dark:text-orange-600 mr-2 mt-0.5">{idx + 1}</span>
                          <div className="flex-1">
                            <p className="text-sm text-zinc-700 dark:text-zinc-300 leading-snug">{issue.text}</p>
                            {issue.suggestion && (
                              <p className="text-xs text-orange-600 dark:text-orange-500 mt-1 italic">💡 {issue.suggestion}</p>
                            )}
                          </div>
                          <button
                            onClick={() => removeIssue(issue.id!)}
                            className="text-orange-400 dark:text-orange-600 hover:text-red-500 dark:hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity ml-1"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Custom Notes */}
                  {aiReviewIssues.filter(i => i.category === 'custom').length > 0 && (
                    <div className="mb-4">
                      <div className="flex items-center mb-2">
                        <MessageSquareWarning className="h-3.5 w-3.5 text-blue-500 mr-1.5" />
                        <span className="text-xs font-semibold text-blue-700 dark:text-blue-400 uppercase tracking-wider">
                          Your Notes ({aiReviewIssues.filter(i => i.category === 'custom').length})
                        </span>
                      </div>
                      {aiReviewIssues.filter(i => i.category === 'custom').map((issue, idx) => (
                        <div key={issue.id} className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-200 dark:border-blue-800/50 mb-2 flex items-start group hover:border-blue-300 dark:hover:border-blue-700 transition-colors">
                          <span className="text-xs font-bold text-blue-400 dark:text-blue-600 mr-2 mt-0.5">{idx + 1}</span>
                          <p className="text-sm text-zinc-700 dark:text-zinc-300 flex-1 leading-snug">{issue.text}</p>
                          <button
                            onClick={() => removeIssue(issue.id!)}
                            className="text-blue-400 dark:text-blue-600 hover:text-red-500 dark:hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity ml-1"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* Add custom note */}
              <div className="mt-4 pt-4 border-t border-zinc-200 dark:border-zinc-800">
                <textarea
                  value={customIssue}
                  onChange={e => setCustomIssue(e.target.value)}
                  className="w-full text-sm p-2 border rounded-md mb-2 bg-white dark:bg-zinc-800 border-zinc-300 dark:border-zinc-700 text-zinc-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
                  placeholder="Add your own note..."
                  rows={2}
                />
                <button
                  onClick={addCustomIssue}
                  disabled={!customIssue.trim()}
                  className="w-full text-xs bg-zinc-200 dark:bg-zinc-700 hover:bg-zinc-300 dark:hover:bg-zinc-600 text-zinc-700 dark:text-zinc-300 py-1.5 rounded transition-colors disabled:opacity-50"
                >
                  Add Note
                </button>
              </div>
            </div>

            {/* Bottom action buttons */}
            <div className="p-4 bg-white dark:bg-zinc-900 border-t border-zinc-200 dark:border-zinc-800">
              {(() => {
                const { aiFixable, custom, humanRequired } = getIssueCounts();
                const canApply = aiFixable.length + custom.length;
                return (
                  <>
                    <button
                      onClick={applyFeedback}
                      disabled={canApply === 0 || aiReviewLoading}
                      className="w-full flex justify-center items-center py-2.5 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400 border border-yellow-200 dark:border-yellow-900/30 hover:bg-yellow-100 dark:hover:bg-yellow-900/30 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed mb-2"
                    >
                      {aiReviewLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin"/>
                      ) : (
                        <>
                          <Sparkles className="h-4 w-4 mr-2"/>
                          Apply AI Fixes {canApply > 0 && `(${canApply})`}
                        </>
                      )}
                    </button>

                    {humanRequired.length > 0 && (
                      <p className="text-xs text-orange-600 dark:text-orange-400 text-center mb-3">
                        {humanRequired.length} item{humanRequired.length > 1 ? 's' : ''} need{humanRequired.length === 1 ? 's' : ''} your manual attention
                      </p>
                    )}

                    <button
                      onClick={handleFinishReview}
                      className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-500 text-white rounded-lg text-sm font-medium transition-colors shadow-sm"
                    >
                      Finish Review <ArrowRight className="h-4 w-4 inline ml-1" />
                    </button>
                  </>
                );
              })()}
            </div>
          </div>
        )}
      </div>

      {/* New Content Dialog - Matrix Selector */}
      {showNewContentDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-zinc-900 rounded-xl shadow-sm w-full max-w-xl p-6 border border-zinc-200 dark:border-zinc-800 animate-fade-in animate-slide-in-down">
            <h3 className="text-lg font-semibold text-zinc-900 dark:text-white mb-2">
              Add Content Items
            </h3>
            <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-4">
              Select platform and language combinations. Click row/column headers to select all.
            </p>

            {/* Platform × Language Matrix */}
            <div className="mb-4">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th className="p-2"></th>
                    {(SUPPORTED_LANGUAGES).map((lang) => (
                      <th key={lang} className="p-2 text-center">
                        <button
                          onClick={() => toggleLanguage(lang)}
                          className="text-xs font-semibold text-zinc-600 dark:text-zinc-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                        >
                          {LANGUAGE_LABELS[lang]}
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {SUPPORTED_PLATFORMS.filter(p => p !== 'all').map((platform) => (
                    <tr key={platform}>
                      <td className="p-2">
                        <button
                          onClick={() => togglePlatform(platform)}
                          className={`flex items-center space-x-2 px-2 py-1 rounded hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors ${
                            platform === 'tv'
                              ? 'text-blue-500'
                              : platform === 'radio'
                              ? 'text-orange-500'
                              : platform === 'web'
                              ? 'text-green-500'
                              : 'text-purple-500'
                          }`}
                        >
                          {getPlatformIcon(platform)}
                          <span className="text-xs font-semibold">{platform.toUpperCase()}</span>
                        </button>
                      </td>
                      {(SUPPORTED_LANGUAGES).map((lang) => {
                        const key = `${platform}:${lang}`;
                        const exists = comboExists(platform, lang);
                        const isSelected = selectedCombos.has(key);

                        return (
                          <td key={key} className="p-2 text-center">
                            <button
                              onClick={() => !exists && toggleCombo(platform, lang)}
                              disabled={exists}
                              className={`w-10 h-10 rounded-lg border-2 transition-all ${
                                exists
                                  ? 'bg-zinc-100 dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700 cursor-not-allowed opacity-50'
                                  : isSelected
                                  ? 'bg-blue-500 border-blue-500 text-white'
                                  : 'border-zinc-200 dark:border-zinc-700 hover:border-blue-400 dark:hover:border-blue-500'
                              }`}
                              title={exists ? 'Already exists' : isSelected ? 'Selected' : 'Click to select'}
                            >
                              {exists ? (
                                <CheckCircle className="h-4 w-4 mx-auto text-zinc-400" />
                              ) : isSelected ? (
                                <CheckCircle className="h-4 w-4 mx-auto" />
                              ) : null}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Quick Presets */}
            <div className="mb-4 flex flex-wrap gap-2">
              <span className="text-xs text-zinc-500 dark:text-zinc-400 self-center mr-2">Quick:</span>
              <button
                onClick={() => {
                  // Standard: TV Dari, Web all 3, Social all 3
                  setSelectedCombos(new Set([
                    'tv:fa-AF',
                    'web:fa-AF', 'web:ps-AF', 'web:eng',
                    'social:fa-AF', 'social:ps-AF', 'social:eng'
                  ]));
                }}
                className="px-2 py-1 text-xs bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 rounded hover:bg-zinc-200 dark:hover:bg-zinc-700"
              >
                Standard Package
              </button>
              <button
                onClick={() => {
                  // Full: TV/Radio Dari+Pashto, Web/Social all 3
                  setSelectedCombos(new Set([
                    'tv:fa-AF', 'tv:ps-AF',
                    'radio:fa-AF', 'radio:ps-AF',
                    'web:fa-AF', 'web:ps-AF', 'web:eng',
                    'social:fa-AF', 'social:ps-AF', 'social:eng'
                  ]));
                }}
                className="px-2 py-1 text-xs bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 rounded hover:bg-zinc-200 dark:hover:bg-zinc-700"
              >
                Full Coverage
              </button>
              <button
                onClick={() => {
                  // Broadcast only: TV/Radio Dari+Pashto
                  setSelectedCombos(new Set([
                    'tv:fa-AF', 'tv:ps-AF',
                    'radio:fa-AF', 'radio:ps-AF'
                  ]));
                }}
                className="px-2 py-1 text-xs bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 rounded hover:bg-zinc-200 dark:hover:bg-zinc-700"
              >
                Broadcast
              </button>
              <button
                onClick={() => {
                  // Digital only: Web/Social all 3
                  setSelectedCombos(new Set([
                    'web:fa-AF', 'web:ps-AF', 'web:eng',
                    'social:fa-AF', 'social:ps-AF', 'social:eng'
                  ]));
                }}
                className="px-2 py-1 text-xs bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 rounded hover:bg-zinc-200 dark:hover:bg-zinc-700"
              >
                Digital
              </button>
              <button
                onClick={() => setSelectedCombos(new Set())}
                className="px-2 py-1 text-xs text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300"
              >
                Clear
              </button>
            </div>

            {/* Selected Summary with auto-types */}
            {selectedCombos.size > 0 && (
              <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-900/30">
                <p className="text-sm font-medium text-blue-700 dark:text-blue-400 mb-2">
                  {selectedCombos.size} content item{selectedCombos.size > 1 ? 's' : ''} will be created:
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {Array.from(selectedCombos)
                    .filter(combo => {
                      const [platform, language] = combo.split(':') as [Platform, LanguageCode];
                      return !comboExists(platform, language);
                    })
                    .map(combo => {
                      const [platform, language] = combo.split(':') as [Platform, LanguageCode];
                      const contentType = getDefaultContentType(platform);
                      return (
                        <span
                          key={combo}
                          className={`inline-flex items-center px-2 py-1 text-xs rounded-full ${
                            platform === 'tv'
                              ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                              : platform === 'radio'
                              ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300'
                              : platform === 'web'
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                              : 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300'
                          }`}
                        >
                          {platform.toUpperCase()}/{LANGUAGE_LABELS[language]} ({contentType})
                        </span>
                      );
                    })}
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowNewContentDialog(false);
                  setSelectedCombos(new Set());
                }}
                className="px-4 py-2 text-sm text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateContentItems}
                disabled={saving || selectedCombos.size === 0}
                className="flex items-center px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Plus className="h-4 w-4 mr-2" />
                )}
                Create {selectedCombos.size > 0 ? `(${selectedCombos.size})` : ''}
              </button>
            </div>
          </div>
        </div>
      )}
      {aiOverlay}
    </div>
  );
};

export default StoryEditor;
